/**
 * Mapowanie procedur do elementów UI
 * 
 * ⚠️ WAŻNE: Przy dodawaniu nowego elementu do menu lub dashboardu:
 * 1. Zidentyfikuj numer procedury wywoływanej przez ten element
 * 2. Dodaj mapowanie tutaj
 * 3. Sprawdź uprawnienia w komponencie używając authStore.hasPermission(procedureNumber)
 */

/**
 * Mapowanie elementów menu górnego do numerów procedur
 */
export const MENU_ITEMS_MAPPING: Record<string, string> = {
  'affairs': '4000',       // Menu: "Sprawy" → proc 4000
  'partners': '4005',      // Menu: "Kontrahenci" → proc 4005
  'products': '2667',      // Menu: "Produkty" → proc 2667
  'new-partner': '4012',   // Menu: "Nowy kontrahent" → proc 4012
  'cart': '2674',          // Menu: "Koszyk" → proc 2674 (złożenie zamówienia)
  'invoice-verification': '2677', // Menu: "Weryfikacja faktury" → proc 2677
  'invoice-verification-foreign': '26771', // Menu: "Weryfikacja faktury obcej" → proc 26771
};

/**
 * Mapowanie elementów dashboardu do numerów procedur
 */
export const DASHBOARD_ITEMS_MAPPING: Record<string, string> = {
  'affairs': '4000',            // Dashboard: "Sprawy" → proc 4000
  'partners': '4005',           // Dashboard: "Kontrahenci" → proc 4005
  'new-partner': '4012',        // Dashboard: "Nowy kontrahent" → proc 4012
  'partner-sales': '4010',      // Dashboard: "Sprzedaż kontrahenta" → proc 4010
  'sales-report': '4014',       // Dashboard: "Raport sprzedaży" → proc 4014
  'sales-analysis': '3012',     // Dashboard: "Analiza sprzedaży" → proc 3012
  'sales-growth': '3012',       // Dashboard: "Wzrost / spadek sprzedaży" → proc 3012 (te same dane co analiza)
  'partner-invoices': '640',    // Dashboard: "Faktury kontrahenta" → proc 640
  'wz': '2615',                 // Dashboard: "Dokumenty Wydania (WZ)" → proc 2615 (lista WZ)
  'wz-expedition': '2615',      // Dashboard: "Ekspedycja WZ" → proc 2615 (te same uprawnienia co WZ)
  'products': '2667',           // Dashboard: "Katalog Produktów" → proc 2667
  'transport-planning': '5400', // Dashboard: "Zaplanuj transport" → proc 5400
  'pricelist': '2667',          // Dashboard: "Cennik kiosku" → proc 2667 (uprawnienie do towarów; gate do zaostrzenia)
};

/**
 * Mapowanie tras routera do numerów procedur (dla navigation guards)
 */
export const ROUTE_PROCEDURE_MAPPING: Record<string, string> = {
  '/employee/affairs': '4000',               // Lista spraw
  '/employee/affair/:id': '4001',            // Szczegóły sprawy / odpowiedź
  '/employee/partners': '4005',              // Lista kontrahentów
  '/employee/partner/:id': '4005',            // Szczegóły kontrahenta (wymaga dostępu do listy)
  '/employee/partner/:id/sales': '4010',     // Sprzedaż kontrahenta
  '/employee/partner/:id/invoices': '640',   // Faktury kontrahenta
  '/employee/partner/:id/invoice/:invoiceId': '640', // Szczegóły faktury (oba wejścia - lista faktur i kafelek ostatniej faktury - też za 640)
  '/employee/partner/:id/products': '2667', // Produkty kontrahenta
  '/employee/new-partner': '4012',           // Nowy kontrahent
  '/employee/sales-report': '4014',          // Raport sprzedaży
  '/employee/sales-analysis': '3012',        // Analiza sprzedaży (wykresy)
  '/employee/sales-growth': '3012',          // Wzrost / spadek sprzedaży (porównanie okresów, te same uprawnienia co analiza)
  '/employee/financial-statement': '4890',   // Sprawozdanie bilans/RZiS ze znaczników JPK_CIT
  '/employee/account-analysis': '4893',      // Analiza kont (karta kontowa + dokument)
  '/employee/rlt': '6015',                   // Zestawienia finansowe (RLT wsadowo: zleć 6015 / wynik 6016)
  '/employee/finance-cockpit': '4897',       // Kokpit finansowy (zestawienie Wynik: KPI 4897 + snapshot magazynu 4898)
  '/employee/wz': '2615',                    // Dokumenty WZ (lista) - proc 2615
  '/employee/wz/:id': '2616',                // Szczegóły WZ - proc 2616
  '/employee/wz-expedition': '2615',        // Ekspedycja WZ - proc 2615
  '/employee/products': '2667',              // Produkty (jeśli będzie włączone)
  '/employee/cart': '2674',                  // Koszyk (złożenie zamówienia)
  '/employee/invoice-verification': '2677',  // Weryfikacja faktury - lista
  '/employee/invoice-verification/:id': '2677', // Weryfikacja faktury - pozycje
  '/employee/invoice-verification-foreign': '26771', // Weryfikacja faktury obcej - lista
  '/employee/invoice-verification-foreign/:id': '26771', // Weryfikacja faktury obcej - pozycje
  '/employee/partner/:id/route-planning': '4006',  // Planowanie trasy z karty kontrahenta
  '/employee/weekly-report': '5300',                // Raport tygodniowy pracy handlowca
  '/employee/team': '5303',                         // Pulpit zarządczy (lista zespołu + delegacja odczytu)
  '/employee/transport-planning': '5400',           // Zaplanuj transport (lista faktur 5400 + VRP)
  '/employee/pricelist': '2667',                    // Cennik kiosku (uprawnienie do towarów; gate do zaostrzenia)
};

/**
 * Pobiera numer procedury dla elementu menu
 */
export function getMenuProcedure(menuItem: string): string | undefined {
  return MENU_ITEMS_MAPPING[menuItem];
}

/**
 * Pobiera numer procedury dla elementu dashboardu
 */
export function getDashboardProcedure(dashboardItem: string): string | undefined {
  return DASHBOARD_ITEMS_MAPPING[dashboardItem];
}

/**
 * Pobiera numer procedury dla trasy (navigation guard): najpierw dokładna ścieżka,
 * potem wzorce z parametrami (np. /employee/partner/:id) - wygrywa pierwszy pasujący.
 * Brak wpisu = trasa bez bramki KiS.
 */
export function getRouteProcedure(path: string): string | undefined {
  const exact = ROUTE_PROCEDURE_MAPPING[path];
  if (exact) return exact;
  for (const [routePattern, procNum] of Object.entries(ROUTE_PROCEDURE_MAPPING)) {
    const pattern = routePattern.replace(/:[^/]+/g, '[^/]+');
    if (new RegExp(`^${pattern}$`).test(path)) return procNum;
  }
  return undefined;
}

